/**
 * useDeviceType Hook - Device detection for responsive rendering
 * Uses MUI breakpoints to determine device type
 */

import { useMediaQuery, useTheme } from "@mui/material";

/**
 * Hook to detect device type based on screen size
 * @returns {Object} Device type information
 * @returns {boolean} isMobile - Screen width < 960px (md breakpoint)
 * @returns {boolean} isTablet - Screen width between 960px and 1280px (md to lg)
 * @returns {boolean} isDesktop - Screen width >= 1280px (lg+)
 * @returns {string} deviceType - 'mobile' | 'tablet' | 'desktop'
 * 
 * @example
 * const { isMobile, isTablet, isDesktop, deviceType } = useDeviceType();
 * if (isMobile) {
 *   return <MobileView />;
 * }
 */
export const useDeviceType = () => {
  const theme = useTheme();
  
  // Mobile: < 960px (below md breakpoint)
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  // Tablet: 960px - 1280px (md to lg)
  const isTablet = useMediaQuery(
    theme.breakpoints.between("md", "lg")
  );
  
  // Desktop: >= 1280px (lg and above)
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  // Determine device type string
  let deviceType = "desktop";
  if (isMobile) {
    deviceType = "mobile";
  } else if (isTablet) {
    deviceType = "tablet";
  }

  return {
    isMobile,
    isTablet,
    isDesktop,
    deviceType,
  };
};

export default useDeviceType;

